<script setup lang="ts">
// Report a message (HRTH-94): this device reveals it, franking key included, so the server
// can check it's exactly what the sender sent. In a space the moderators see it, as do
// the server's admins; reports of direct messages go to the admins only.
import { computed, ref } from 'vue'
import type { ReportReason } from '../api/types'
import type { StoredMessage } from '../storage/db'
import { describeError } from '../api/http'
import { REASONS } from '../lib/reports'
import { displayName } from '../lib/format'
import { useSession } from '../stores/session'
import Modal from './Modal.vue'

const props = defineProps<{ convId: string; msg: StoredMessage }>()
const emit = defineEmits<{ close: [] }>()
const session = useSession()
const reason = ref<ReportReason | null>(null)
const note = ref('')
const busy = ref(false)
const conv = computed(() => session.conversations.get(props.convId))
const who = computed(() => (conv.value?.space_id ? "This space's moderators and this server's admins" : "This server's admins"))

async function send(): Promise<void> {
  if (!reason.value) return
  busy.value = true
  try {
    await session.api.report(
      {
        conversation: props.convId,
        seq: Math.floor(props.msg.seq),
        sender: props.msg.sender.user_id,
        envelope: props.msg.envelope,
        reason: reason.value,
        ...(note.value.trim() ? { note: note.value.trim() } : {}),
      },
      conv.value?.host,
    )
    session.toast('Reported. Thank you')
    emit('close')
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <Modal title="Report message" @close="emit('close')">
    <p class="mb-3 text-sm text-text-secondary">
      Reporting {{ displayName(session.profiles, msg.sender.user_id) }}'s message. {{ who }} will see it, as you see it
      here; {{ displayName(session.profiles, msg.sender.user_id) }} isn't told who reported it.
    </p>
    <form class="space-y-3" data-testid="report-form" @submit.prevent="send">
      <fieldset class="space-y-1">
        <legend class="label">Reason</legend>
        <label v-for="r in REASONS" :key="r.reason" class="flex cursor-pointer items-start gap-2 py-0.5 text-sm">
          <input v-model="reason" type="radio" name="reason" :value="r.reason" class="mt-1" :data-testid="`report-reason-${r.reason}`" />
          <span>
            {{ r.label }}
            <span class="mono block text-text-muted">{{ r.hint }}</span>
          </span>
        </label>
      </fieldset>
      <label class="block">
        <span class="label">Anything to add (optional)</span>
        <textarea v-model="note" class="input min-h-16" maxlength="2000" data-testid="report-note" />
      </label>
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-ghost" @click="emit('close')">Cancel</button>
        <button class="btn-primary" :disabled="busy || !reason" data-testid="report-send">{{ busy ? '…' : 'Report' }}</button>
      </div>
    </form>
  </Modal>
</template>
